import React, { useState, useEffect } from "react";
import { formatDate } from "../../utils/utils";
import { updateMembershipOnboardingCall } from "../../api/apiOperations";
import { Checkbox, TimestampText } from "./styles";

interface OnboardingCheckboxProps {
  membershipId: string;
  isOnboarded: boolean;
  onboardedAt: string | null | undefined;
  isExpired: boolean;
  onUpdate?: () => void;
}

const OnboardingCheckbox: React.FC<OnboardingCheckboxProps> = ({ 
  membershipId, 
  isOnboarded: initialIsOnboarded, 
  onboardedAt: initialOnboardedAt,
  isExpired,
  onUpdate
}) => {
  const [isOnboarded, setIsOnboarded] = useState(initialIsOnboarded);
  const [onboardedAt, setOnboardedAt] = useState(initialOnboardedAt);
  const [isLoading, setIsLoading] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [checkInCode, setCheckInCode] = useState('');
  const [error, setError] = useState('');

  // Update local state when props change
  useEffect(() => {
    setIsOnboarded(initialIsOnboarded);
    setOnboardedAt(initialOnboardedAt);
  }, [initialIsOnboarded, initialOnboardedAt]);

  // A check-in is final: it decides the month the studio is paid in.
  const handleToggle = () => {
    if (isExpired || isOnboarded) return;
    setShowCodeInput(true);
    setError('');
  };

  const submitOnboarding = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const result = await updateMembershipOnboardingCall(membershipId, true, checkInCode.trim());
      
      if (result) {
        setIsOnboarded(true);
        setOnboardedAt(new Date().toISOString());
        setShowCodeInput(false);
        setCheckInCode('');
        setError('');
        
        if (onUpdate) {
          onUpdate();
        }
      }
    } catch (error: any) {
      console.error("Failed to update onboarding status:", error);
      
      // Handle specific error codes
      if (error.response?.data?.code === 'INVALID_CHECKIN_CODE') {
        setError('Invalid check-in code. Please verify and try again.');
      } else if (error.response?.data?.code === 'CHECKIN_CODE_REQUIRED') {
        setError('Check-in code is required to onboard this member.');
      } else {
        setError('Failed to update onboarding status. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeSubmit = () => {
    if (!checkInCode.trim()) {
      setError('Please enter the check-in code.');
      return;
    }
    submitOnboarding();
  };

  const handleCancel = () => {
    setShowCodeInput(false);
    setCheckInCode('');
    setError('');
  };

  // If showing code input, render the input form
  if (showCodeInput) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        gap: '8px',
        minWidth: '200px',
        padding: '8px',
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
        backgroundColor: '#f9f9f9'
      }}>
        <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#333' }}>
          Enter Check-in Code:
        </div>
        <input
          type="text"
          value={checkInCode}
          onChange={(e) => setCheckInCode(e.target.value)}
          placeholder="e.g., ABC123"
          style={{
            padding: '4px 6px',
            border: '1px solid #ccc',
            borderRadius: '3px',
            fontSize: '12px',
            outline: 'none'
          }}
          disabled={isLoading}
          onKeyPress={(e) => e.key === 'Enter' && handleCodeSubmit()}
          autoFocus
        />
        {error && (
          <div style={{ 
            fontSize: '11px', 
            color: '#d32f2f',
            marginTop: '2px'
          }}>
            {error}
          </div>
        )}
        <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
          <button
            onClick={handleCodeSubmit}
            disabled={isLoading || !checkInCode.trim()}
            style={{
              padding: '4px 8px',
              fontSize: '11px',
              backgroundColor: checkInCode.trim() ? '#4caf50' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '3px',
              cursor: checkInCode.trim() ? 'pointer' : 'not-allowed'
            }}
          >
            {isLoading ? 'Verifying...' : 'Confirm'}
          </button>
          <button
            onClick={handleCancel}
            disabled={isLoading}
            style={{
              padding: '4px 8px',
              fontSize: '11px',
              backgroundColor: '#757575',
              color: 'white',
              border: 'none',
              borderRadius: '3px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // Normal checkbox view
  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: '10px',
      minHeight: '24px',
    }}>
      <Checkbox
        type="checkbox"
        checked={isOnboarded}
        onChange={handleToggle}
        disabled={isExpired || isLoading || isOnboarded}
        title={isOnboarded ? "Checked in" : undefined}
      />
      <div style={{ width: '70px', textAlign: 'left' }}>
        <TimestampText style={{ visibility: onboardedAt ? 'visible' : 'hidden' }}>
          {onboardedAt ? formatShortDate(onboardedAt) : " "}
        </TimestampText>
      </div>
    </div>
  );  
};

const formatShortDate = (dateString: string) => {
  const date = new Date(dateString);
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
};

export default OnboardingCheckbox;